import { fetchBillingDashboardStats } from '@/modules/billing/services';
import { fetchCalendarDashboardStats } from '@/modules/calendar/services';
import { fetchCommercialDashboardStats } from '@/modules/commercial/services';
import { fetchMaintenanceDashboardStats } from '@/modules/maintenance/services/maintenanceDashboardStats';
import { fetchMovementDashboardStats } from '@/services/inventoryMovements/movementDashboardStats';
import { fetchLogisticsDashboardStats } from '@/modules/logistics/services';
import { fetchInventoryDashboardStats } from '@/modules/inventory/services';
import { fetchReservationDashboardStats } from '@/modules/reservations/services';

export type DashboardStats = {
  inventory: Awaited<ReturnType<typeof fetchInventoryDashboardStats>>;
  commercial: Awaited<ReturnType<typeof fetchCommercialDashboardStats>>;
  billing: Awaited<ReturnType<typeof fetchBillingDashboardStats>>;
  reservations: Awaited<ReturnType<typeof fetchReservationDashboardStats>>;
  calendar: Awaited<ReturnType<typeof fetchCalendarDashboardStats>>;
  logistics: Awaited<ReturnType<typeof fetchLogisticsDashboardStats>>;
  maintenance: Awaited<ReturnType<typeof fetchMaintenanceDashboardStats>>;
  movements: Awaited<ReturnType<typeof fetchMovementDashboardStats>>;
};

export type DashboardModuleId = keyof DashboardStats;

export type DashboardModuleFailure = {
  module: DashboardModuleId;
  code: string;
  message: string;
  hint: string;
};

export type PartialDashboardStats = Partial<DashboardStats>;

export type DashboardStatsLoadResult = {
  stats: PartialDashboardStats;
  failures: DashboardModuleFailure[];
};

const MODULE_LABELS: Record<DashboardModuleId, string> = {
  inventory: 'Inventario',
  commercial: 'Comercial',
  billing: 'Facturación',
  reservations: 'Reservas',
  calendar: 'Agenda',
  logistics: 'Logística',
  maintenance: 'Mantenimiento',
  movements: 'Movimientos',
};

function formatFirestoreFailure(
  module: DashboardModuleId,
  error: unknown,
): DashboardModuleFailure {
  const label = MODULE_LABELS[module];
  if (
    error &&
    typeof error === 'object' &&
    'code' in error &&
    typeof (error as { code: unknown }).code === 'string'
  ) {
    const { code, message } = error as { code: string; message?: string };
    const msg = message?.trim() || 'Error de Firestore';
    switch (code) {
      case 'permission-denied':
        return {
          module,
          code,
          message: `${label}: ${msg}`,
          hint: 'Desplegá reglas e índices: pnpm firestore:deploy (ver docs/PAGES_DEPLOY.md).',
        };
      case 'failed-precondition':
        return {
          module,
          code,
          message: `${label}: falta un índice compuesto`,
          hint: 'Firebase Console → Firestore → Indexes, o ejecutá pnpm firestore:deploy.',
        };
      default:
        return {
          module,
          code,
          message: `${label}: ${msg}`,
          hint: '',
        };
    }
  }
  const fallback =
    error instanceof Error ? error.message : 'Error desconocido';
  return {
    module,
    code: 'unknown',
    message: `${label}: ${fallback}`,
    hint: '',
  };
}

export function formatDashboardFailuresSummary(
  failures: DashboardModuleFailure[],
): string | null {
  if (failures.length === 0) return null;
  const lines = failures.map((f) => {
    const hint = f.hint ? ` ${f.hint}` : '';
    return `${f.message} (${f.code}).${hint}`;
  });
  return `Algunos módulos no cargaron: ${lines.join(' ')}`;
}

export async function fetchDashboardStats(): Promise<DashboardStatsLoadResult> {
  const [
    inventoryR,
    commercialR,
    billingR,
    reservationsR,
    calendarR,
    logisticsR,
    maintenanceR,
    movementsR,
  ] = await Promise.allSettled([
    fetchInventoryDashboardStats(),
    fetchCommercialDashboardStats(),
    fetchBillingDashboardStats(),
    fetchReservationDashboardStats(),
    fetchCalendarDashboardStats(),
    fetchLogisticsDashboardStats(),
    fetchMaintenanceDashboardStats(),
    fetchMovementDashboardStats(),
  ]);

  const stats: PartialDashboardStats = {};
  const failures: DashboardModuleFailure[] = [];

  if (inventoryR.status === 'fulfilled') {
    stats.inventory = inventoryR.value;
  } else {
    failures.push(formatFirestoreFailure('inventory', inventoryR.reason));
    console.error('[dashboard] inventory stats failed', inventoryR.reason);
  }

  if (commercialR.status === 'fulfilled') {
    stats.commercial = commercialR.value;
  } else {
    failures.push(formatFirestoreFailure('commercial', commercialR.reason));
    console.error('[dashboard] commercial stats failed', commercialR.reason);
  }

  if (billingR.status === 'fulfilled') {
    stats.billing = billingR.value;
  } else {
    failures.push(formatFirestoreFailure('billing', billingR.reason));
    console.error('[dashboard] billing stats failed', billingR.reason);
  }

  if (reservationsR.status === 'fulfilled') {
    stats.reservations = reservationsR.value;
  } else {
    failures.push(formatFirestoreFailure('reservations', reservationsR.reason));
    console.error('[dashboard] reservations stats failed', reservationsR.reason);
  }

  if (calendarR.status === 'fulfilled') {
    stats.calendar = calendarR.value;
  } else {
    failures.push(formatFirestoreFailure('calendar', calendarR.reason));
    console.error('[dashboard] calendar stats failed', calendarR.reason);
  }

  if (logisticsR.status === 'fulfilled') {
    stats.logistics = logisticsR.value;
  } else {
    failures.push(formatFirestoreFailure('logistics', logisticsR.reason));
    console.error('[dashboard] logistics stats failed', logisticsR.reason);
  }

  if (maintenanceR.status === 'fulfilled') {
    stats.maintenance = maintenanceR.value;
  } else {
    failures.push(formatFirestoreFailure('maintenance', maintenanceR.reason));
    console.error('[dashboard] maintenance stats failed', maintenanceR.reason);
  }

  if (movementsR.status === 'fulfilled') {
    stats.movements = movementsR.value;
  } else {
    failures.push(formatFirestoreFailure('movements', movementsR.reason));
    console.error('[dashboard] movements stats failed', movementsR.reason);
  }

  return { stats, failures };
}
